import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import GlobalContext from '../../context/store';
import { AffContext } from '../../context/AffiliateContext';
import { buildCartFees, EMPTY_CUSTOMER_INFO, formatPhoneInput, getVisibleInventory, isTicketEventOwnedByCompany, requiresPayment, validateCustomerInfo } from './util';
import { handleCheckoutPay } from './handlePay';
import TicketExpressCheckout from './TicketExpressCheckout';

const CHECKOUT_STEPS = ['Tickets', 'Details', 'Payment'];

function toTier(ticket, company, event, aff) {
  const remaining = Math.max(Number(ticket.totalQuantity || 0) - Number(ticket.soldQuantity || 0), 0);
  const price = Number(ticket.price || 0);
  const fees = buildCartFees([{ id: ticket.ticketName, price, extraFee: ticket.extraFee || 0 }], { [ticket.ticketName]: 1 }, company?.application_fee ?? 3.5, 0.029, 0.3, event?.host_assist_fee_percent || 0);
  return {
    id: ticket.ticketName,
    name: ticket.ticketName,
    description: ticket.ticketDescription || '',
    price,
    afterFee: fees.total,
    maxQty: Math.min(Number(ticket.maxPurchaseQuantity || 10), remaining),
    soldOut: remaining <= 0,
    remaining,
    extraFee: Number(ticket.extraFee || 0),
    badge: ticket.valueTitle || (ticket.isBestValue ? 'Best value' : ''),
    affiliate: Boolean(aff && ticket?.affiliate?.slug === aff),
  };
}

function formatTotal(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return 'Free';
  return `$${value.toFixed(2)}`;
}

function ModalShell({ title, subtitle, children, onClose, unavailable = false, dialogRef }) {
  return createPortal(
    <div className="ticket-modal">
      <div className="ticket-modal__dialog" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="ticket-checkout-title">
        <div className="ticket-modal__content">
          <div className="ticket-modal__header">
            <div>
              <p className="ticket-eyebrow">Tickets</p>
              <h2 id="ticket-checkout-title" className="ticket-modal__title">{title}</h2>
              {subtitle && <p className="ticket-modal__meta">{subtitle}</p>}
            </div>
            <button type="button" className="ticket-modal__close" aria-label="Close ticket checkout" onClick={onClose}>&times;</button>
          </div>
          {unavailable ? <div className="ticket-modal__body">{children}</div> : children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function CheckoutModal({ event, onClose }) {
  const { company } = useContext(GlobalContext);
  const { aff, affId } = useContext(AffContext);
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [cart, setCart] = useState({});
  const [customerInfo, setCustomerInfo] = useState(EMPTY_CUSTOMER_INFO);
  const [errors, setErrors] = useState({});
  const [paymentError, setPaymentError] = useState('');
  const [isPaying, setIsPaying] = useState(false);
  const dialogRef = useRef(null);
  const openerRef = useRef(null);
  const eventBelongsToCompany = isTicketEventOwnedByCompany(event, company);

  const tiers = useMemo(() => (
    getVisibleInventory(event?.ticketinventory, aff).map((ticket) => toTier(ticket, company, event, aff))
  ), [aff, company, event]);
  const fees = useMemo(() => buildCartFees(tiers, cart, company?.application_fee ?? 3.5, 0.029, 0.3, event?.host_assist_fee_percent || 0), [cart, company, event, tiers]);
  const hasTickets = Object.values(cart).some((qty) => qty > 0);
  const selectedTiers = tiers.filter((tier) => (cart[tier.id] || 0) > 0);
  const subtitle = [
    [event?.start_date_display, event?.start_time_display].filter(Boolean).join(' · '),
    event?.organization?.name || event?.address || '',
  ].filter(Boolean).join(' · ');

  useEffect(() => {
    const viewport = window.visualViewport;
    const media = window.matchMedia?.('(max-width: 575px)');
    const content = () => dialogRef.current?.querySelector('.ticket-modal__content');
    const sync = () => {
      const sheet = content();
      if (!sheet) return;
      if (!media?.matches || !viewport) {
        sheet.style.marginTop = '';
        sheet.style.height = '';
        sheet.style.maxHeight = '';
        sheet.style.minHeight = '';
        return;
      }
      sheet.style.marginTop = `${viewport.offsetTop}px`;
      sheet.style.height = `${viewport.height}px`;
      sheet.style.maxHeight = `${viewport.height}px`;
      sheet.style.minHeight = `${viewport.height}px`;
    };
    sync();
    viewport?.addEventListener('resize', sync);
    viewport?.addEventListener('scroll', sync);
    media?.addEventListener?.('change', sync);
    window.addEventListener('resize', sync);
    return () => {
      viewport?.removeEventListener('resize', sync);
      viewport?.removeEventListener('scroll', sync);
      media?.removeEventListener?.('change', sync);
      window.removeEventListener('resize', sync);
    };
  }, []);

  useEffect(() => {
    openerRef.current = document.activeElement;
    const pageWrapper = document.getElementById('page-wrapper');
    const priorAriaHidden = pageWrapper?.getAttribute('aria-hidden');
    const priorOverflow = document.body.style.overflow;
    if (pageWrapper) pageWrapper.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = 'hidden';
    const focusModal = window.requestAnimationFrame(() => {
      dialogRef.current?.querySelector('button:not([disabled]), input:not([disabled]), a[href]')?.focus();
    });
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]') || []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusModal);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = priorOverflow;
      if (pageWrapper) {
        if (priorAriaHidden === null) pageWrapper.removeAttribute('aria-hidden');
        else pageWrapper.setAttribute('aria-hidden', priorAriaHidden);
      }
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
    };
  }, [onClose]);

  if (!eventBelongsToCompany) {
    return (
      <ModalShell title="Ticket checkout unavailable" onClose={onClose} dialogRef={dialogRef} unavailable>
        <div className="ticket-state ticket-state--error">
          <p>This event is not available through Dollhouse Mermaid ticket checkout.</p>
        </div>
      </ModalShell>
    );
  }

  function updateQuantity(tier, delta) {
    setCart((previous) => {
      const next = Math.max(0, Math.min(tier.maxQty, (previous[tier.id] || 0) + delta));
      return { ...previous, [tier.id]: next };
    });
  }

  function continueCustomerInfo() {
    const result = validateCustomerInfo(customerInfo);
    if (!result.isValid) {
      setErrors(result.errors);
      return;
    }
    setCustomerInfo(result.values);
    setErrors({});
    setStep(3);
  }

  async function pay() {
    await handleCheckoutPay({
      customerInfo,
      cart,
      currentTicket: event,
      company,
      stripe,
      elements,
      getTotal: () => fees.total,
      onValidationError: (nextErrors) => { setErrors(nextErrors); setStep(2); },
      onPaymentError: setPaymentError,
      onPaymentStart: () => setIsPaying(true),
      onPaymentEnd: () => setIsPaying(false),
      onSuccess: (response) => { onClose(); navigate(`/events/confirmation/${response.user}/${response.event}`); },
      onFailure: () => setPaymentError('We could not complete your ticket order. Please try again.'),
      affId,
    });
  }

  return (
    <ModalShell title={event?.name || 'Tickets'} subtitle={subtitle} onClose={onClose} dialogRef={dialogRef}>
      <div className="ticket-modal__body">
        <ol className="ticket-steps" aria-label="Checkout progress">
          {CHECKOUT_STEPS.map((label, index) => {
            const number = index + 1;
            const state = step === number ? ' is-current' : step > number ? ' is-done' : '';
            return (
              <li key={label} className={`ticket-steps__item${state}`} aria-current={step === number ? 'step' : undefined}>
                <span>{number}</span>
                {label}
              </li>
            );
          })}
        </ol>
        {step === 1 && <>
          {tiers.length === 0 && <div className="ticket-state"><p>No tickets are on sale right now.</p></div>}
          {tiers.map((tier) => (
            <div key={tier.id} className={`ticket-tier${tier.soldOut ? ' is-sold-out' : ''}${tier.affiliate ? ' is-affiliate' : ''}`}>
              <div className="ticket-tier__info">
                {tier.badge && <p className="ticket-tier__badge">{tier.badge}</p>}
                <h3 className="ticket-tier__name">{tier.name}</h3>
                {tier.description && <p className="ticket-tier__description">{tier.description}</p>}
                <p className="ticket-tier__price">
                  <strong>{tier.soldOut ? 'Sold out' : formatTotal(tier.price === 0 ? 0 : tier.afterFee)}</strong>
                  {!tier.soldOut && tier.price > 0 && <span> per ticket, fees included</span>}
                </p>
                {!tier.soldOut && tier.remaining > 0 && tier.remaining <= 8 && (
                  <p className="ticket-tier__stock">Only {tier.remaining} left</p>
                )}
              </div>
              {tier.soldOut ? <span className="ticket-tier__soldout">Sold out</span> : (
                <div className="ticket-quantity" aria-label={`${tier.name} quantity`}>
                  <button className="ticket-quantity__button" type="button" aria-label={`Remove one ${tier.name} ticket`} disabled={!cart[tier.id]} onClick={() => updateQuantity(tier, -1)}>−</button>
                  <span className="ticket-quantity__value" aria-live="polite">{cart[tier.id] || 0}</span>
                  <button className="ticket-quantity__button" type="button" aria-label={`Add one ${tier.name} ticket`} disabled={tier.soldOut || (cart[tier.id] || 0) >= tier.maxQty} onClick={() => updateQuantity(tier, 1)}>+</button>
                </div>
              )}
            </div>
          ))}
        </>}
        {step === 2 && (
          <form className="ticket-form" onSubmit={(submitEvent) => { submitEvent.preventDefault(); continueCustomerInfo(); }} noValidate>
            <p className="ticket-form__intro">Where should we send the tickets?</p>
            <div className="ticket-field-row">
              <div>
                <label className="ticket-field"><span className="ticket-field__label">First name</span><input type="text" autoComplete="given-name" aria-invalid={Boolean(errors.firstName)} aria-describedby={errors.firstName ? 'ticket-first-name-error' : undefined} value={customerInfo.firstName} onChange={(event) => setCustomerInfo({ ...customerInfo, firstName: event.target.value })} /></label>
                {errors.firstName && <p id="ticket-first-name-error" className="ticket-error" role="alert">{errors.firstName}</p>}
              </div>
              <div>
                <label className="ticket-field"><span className="ticket-field__label">Last name</span><input type="text" autoComplete="family-name" aria-invalid={Boolean(errors.lastName)} aria-describedby={errors.lastName ? 'ticket-last-name-error' : undefined} value={customerInfo.lastName} onChange={(event) => setCustomerInfo({ ...customerInfo, lastName: event.target.value })} /></label>
                {errors.lastName && <p id="ticket-last-name-error" className="ticket-error" role="alert">{errors.lastName}</p>}
              </div>
            </div>
            <label className="ticket-field"><span className="ticket-field__label">Email</span><input type="email" autoComplete="email" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'ticket-email-error' : undefined} value={customerInfo.email} onChange={(event) => setCustomerInfo({ ...customerInfo, email: event.target.value })} /></label>
            {errors.email && <p id="ticket-email-error" className="ticket-error" role="alert">{errors.email}</p>}
            <label className="ticket-field"><span className="ticket-field__label">Phone</span><input type="tel" autoComplete="tel" inputMode="numeric" placeholder="(415) 555-0100" aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'ticket-phone-error' : undefined} value={customerInfo.phone} onChange={(event) => setCustomerInfo((current) => ({ ...current, phone: formatPhoneInput(event.target.value, current.phone) }))} /></label>
            {errors.phone && <p id="ticket-phone-error" className="ticket-error" role="alert">{errors.phone}</p>}
            <label className="ticket-consent"><input type="checkbox" aria-invalid={Boolean(errors.termsAccepted)} aria-describedby={errors.termsAccepted ? 'ticket-terms-error' : undefined} checked={customerInfo.termsAccepted} onChange={(event) => setCustomerInfo({ ...customerInfo, termsAccepted: event.target.checked })} /><span>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">terms and conditions</a>.</span></label>
            {errors.termsAccepted && <p id="ticket-terms-error" className="ticket-error" role="alert">{errors.termsAccepted}</p>}
          </form>
        )}
        {step === 3 && <>
          <ul className="ticket-summary">
            {selectedTiers.map((tier) => (
              <li key={tier.id}>
                <span>{cart[tier.id]} × {tier.name}</span>
              </li>
            ))}
          </ul>
          {requiresPayment(fees.total) ? <>
            <TicketExpressCheckout
              cart={cart}
              totalAmountDue={fees.total}
              event={event}
              customerInfo={customerInfo}
              affId={affId}
              onSuccess={(response) => { onClose(); navigate(`/events/confirmation/${response.user}/${response.event}`); }}
              onError={setPaymentError}
              onPaymentStart={() => setIsPaying(true)}
              onPaymentEnd={() => setIsPaying(false)}
            />
            <div className="ticket-payment-card">
              <p className="ticket-field__label">Card</p>
              <CardElement options={{
                style: {
                  base: {
                    fontFamily: 'Muli, sans-serif',
                    fontSize: '16px',
                    color: '#556a89',
                    '::placeholder': { color: '#a89890' },
                  },
                  invalid: { color: '#9f4d5e' },
                },
              }}
              />
            </div>
          </> : <div className="ticket-state"><p>No payment is required. Claim the tickets and they will be emailed to you.</p></div>}
          {paymentError && <p className="ticket-error ticket-error--payment" role="alert">{paymentError}</p>}
        </>}
      </div>
      <div className="ticket-modal__footer">
        <p className="ticket-modal__total">{hasTickets ? <>Total <strong>{formatTotal(fees.total)}</strong></> : <span>Select a ticket to continue</span>}</p>
        <div className="ticket-modal__actions">
          {step > 1 && <button type="button" className="ticket-secondary" onClick={() => setStep(step - 1)} disabled={isPaying}>Back</button>}
          {step === 1 && <button type="button" className="myButn" disabled={!hasTickets} onClick={() => setStep(2)}>Continue</button>}
          {step === 2 && <button type="button" className="myButn" onClick={continueCustomerInfo}>Continue to payment</button>}
          {step === 3 && <button type="button" className="myButn" disabled={isPaying} onClick={pay}>{isPaying ? 'Processing...' : requiresPayment(fees.total) ? 'Pay and get tickets' : 'Claim free tickets'}</button>}
        </div>
      </div>
    </ModalShell>
  );
}
